import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { skillsApi } from '../../../../../../services/api';
import { useAlert } from '../../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../../common/LoadingSpinner';
import { SKILL_TYPES, getStatusColor, getStatusText, getStatusTextColor } from '../../../../../../constants/skillTypes';

const GradesThreshold = ({ isTechnicalAssessment }) => {
  const { versionId } = useParams();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [thresholds, setThresholds] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await skillsApi.getTemplateById(versionId);
        // Sort thresholds by grade order
        const sortedThresholds = data.thresholds.sort((a, b) => a.grade.order - b.grade.order);
        // Add a gradeLabel field to each threshold
        const processedThresholds = sortedThresholds.map(threshold => ({
          ...threshold,
          gradeLabel: threshold.grade?.label || 'Unknown Grade'
        }));

        setThresholds(processedThresholds);
      } catch (error) {
        showAlert('Failed to fetch thresholds', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [versionId, showAlert]);

  const columns = [
    {
      field: 'gradeLabel',
      headerName: 'Grade',
      width: 150,
      editable: false
    },
    {
      field: 'threshold',
      headerName: 'Threshold',
      type: 'number',
      width: 150,
      editable: true
    },
    {
      field: 'yearsOfExperience',
      headerName: 'Years of Experience',
      type: 'number',
      width: 200,
      editable: true
    }
  ];

  const handleProcessRowUpdate = async (newRow, oldRow) => {
    // Check if threshold or yearsOfExperience actually changed
    if (newRow.threshold === oldRow.threshold && newRow.yearsOfExperience === oldRow.yearsOfExperience) {
      return oldRow; // Return old row without making API call if nothing changed
    }

    try {
      await skillsApi.updateSkillsTemplateThreshold(newRow.id, {
        threshold: newRow.threshold,
        yearsOfExperience: newRow.yearsOfExperience
      });
      showAlert('Threshold updated successfully', 'success');
      return newRow;
    } catch (error) {
      showAlert('Failed to update threshold', 'error', error);
      return oldRow;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const skillType = isTechnicalAssessment ? SKILL_TYPES.TECHNICAL : SKILL_TYPES.SOFT;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, justifyContent: 'left' }}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          {getStatusText(skillType)} Skills
        </Typography>
        <Box sx={{ 
          height: 'calc((100vh - 250px) / 2)', 
          width: '100%',
          backgroundColor: getStatusColor(skillType),
          borderRadius: '8px',
          padding: '16px',
          '& .MuiDataGrid-root': {
            backgroundColor: 'white',
            borderRadius: '4px'
          },
          '& .MuiDataGrid-cell--editing': {
            bgcolor: 'rgb(255,215,115, 0.19)',
            '& .MuiInputBase-root': {
              height: '100%'
            }
          }
        }}>
          <DataGrid
            rows={thresholds}
            columns={columns}
            hideFooter
            processRowUpdate={handleProcessRowUpdate}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GradesThreshold; 